<template>
	<div class="shop">
		<GemOffersSection v-if="gemOffers" isOnSale />

		<div
			class="shop__item"
			v-for="offer in starterOffers ?? []"
			:key="offer.store_id"
		>
			<PremiumShopItem
				:offer="offer"
				@openPopup="handlePopupOpen(offer.store_id)"
			/>
		</div>

		<div class="shop-section">
			<InGameShopItem
				v-if="availableGemForCoinsOffer"
				:key="availableGemForCoinsOffer.id"
				:contentData="availableGemForCoinsOffer"
				@buyPackage="handleBuyInGamePackage"
			/>
		</div>

		<div class="shop-section">
			<InGameShopItem
				:contentData="
					GAME_CURRENCY_OFFERS_CONFIG.ACTION_FOR_GEMS_OFFERS.get(1)
				"
				@buyPackage="handleBuyInGamePackage"
			/>
			<InGameShopItem
				v-for="[
					key,
					offer,
				] in GAME_CURRENCY_OFFERS_CONFIG.SUPER_CLICKER_OFFERS"
				:key="key"
				:contentData="{
					...offer,
					replaceValue:
						SuperClickerEnum.TEN +
						getUpgradeCurrentReplaceValueById(
							UpgradeIds.butterfingers,
						),
				}"
				@buyPackage="handleBuyInGamePackage"
			/>

			<div
				class="shop-section"
				:data-section-key="ResourceType.AD_SKIPPER"
			>
				<InGameShopItem
					v-for="[
						key,
						offer,
					] in GAME_CURRENCY_OFFERS_CONFIG.AD_SKIPPER_OFFERS"
					id="ad-skipper"
					:key="key"
					:contentData="offer"
					@buyPackage="handleBuyInGamePackage"
				/>
			</div>

			<div
				v-for="offer in resourcesWithKeys"
				:key="offer.resourceKey"
				:data-section-key="offer.resourceKey"
				class="ref-section-wrapper"
			>
				<InGameShopItem
					:key="offer.resourceKey"
					:contentData="offer"
					@buyPackage="handleBuyInGamePackage"
				/>
			</div>
		</div>

		<div class="shop-section" v-if="mutatedFreePack">
			<InGameShopItem
				:contentData="mutatedFreePack"
				:showNotification="freeOfferAvailable"
				@buyPackage="claimFreePack"
			/>
		</div>
	</div>
</template>

<script lang="ts" setup>
import GemOffersSection from "@/components/shop/GemOffersSection.vue";
import InGameShopItem from "@/components/shop/cards/InGameShopItem.vue";
import PremiumShopItem from "@/components/shop/cards/PremiumShopItem.vue";
import {
	GAME_CURRENCY_OFFERS_CONFIG,
	type Offer,
} from "@/game/config/gameCurrencyOffers";
import { ResourceType } from "@/game/entities/config/resources";
import { UpgradeIds } from "@/game/entities/config/upgrades";
import { getUpgradeCurrentReplaceValueById } from "@/game/utils/upgrades";
import useEmitter, { EmitterEvents } from "@/hooks/useEmitter";
import useSocketService from "@/hooks/useSocketService";
import {
	OfferAnalyticsClickSource,
	OfferAnalyticsType,
} from "@/libs/SocketService";
import { CalculationType, OfferProvider } from "@/services/OfferProvider";
import { PaymentProvider } from "@/services/PaymentProvider";
import useResourcesStore from "@/store/useResourcesStore";
import { SuperClickerEnum } from "@/store/useRuntimeStore";
import useShopStore from "@/store/useShopStore";
import useUserStore from "@/store/useUserStore";
import type { ConversionOffer } from "@/types/shop";
import {
	executeScroll,
	pendingScrollTarget,
	scrollToRef,
} from "@/utils/scrollTo";
import { storeToRefs } from "pinia";
import { computed, markRaw, nextTick, watch } from "vue";
import { useRoute } from "vue-router";

const emitter = markRaw(useEmitter());

const mutatedFreePack = OfferProvider.regularFreePack;
const freeOfferAvailable = computed(
	(): boolean => OfferProvider.freeRegularOfferAvailable,
);

const handleBuyInGamePackage = (offer: Offer): void => {
	PaymentProvider.buyInGamePackage(offer);
};

const userStore = useUserStore();
const { inGameGemPurchaseForCoinsOfferId } = storeToRefs(userStore);

const shopStore = useShopStore();
const { starterOffers, gemOffers } = storeToRefs(shopStore);

const resourcesStore = useResourcesStore();
const { increase } = resourcesStore;

const availableGemForCoinsOffer = computed(
	(): Offer | null =>
		GAME_CURRENCY_OFFERS_CONFIG.GEM_FOR_COINS_OFFERS.get(
			inGameGemPurchaseForCoinsOfferId.value + 1,
		) || null,
);

const resourcesForGemsOffers = computed((): Array<Offer> | null => {
	return OfferProvider.resourcesForGemsOffers.value || null;
});

const route = useRoute();

interface ExtendedOffer extends Offer {
	resourceKey: string;
}

const resourcesWithKeys = computed((): Array<ExtendedOffer> => {
	return (
		resourcesForGemsOffers.value?.map(
			(offer: Offer): ExtendedOffer => ({
				...offer,
				resourceKey: offer?.value
					? Object.keys(offer.value)[0]
					: "unknown",
			}),
		) || []
	);
});

watch(
	resourcesForGemsOffers,
	async (newVal: Array<Offer> | null): Promise<void> => {
		if (newVal && newVal.length > 0 && pendingScrollTarget.value) {
			await nextTick(); // Waiting for DOM to be rendered

			const target = pendingScrollTarget.value;
			const el = document.querySelector(
				`[data-section-key="${target}"]`,
			) as HTMLElement;

			if (el) {
				executeScroll(el);
				pendingScrollTarget.value = null; // Target reset
			}
		}
	},
	{ deep: true },
);

const handlePopupOpen = (storeId: string): void => {
	useSocketService().emitOfferAnalyticsSave({
		type: OfferAnalyticsType.OFFER_CLICK,
		clickSource: OfferAnalyticsClickSource.STORE ?? "UNKNOWN",
		storeId: storeId ?? "UNKNOWN",
		offerName: shopStore.getItemByStoreId(storeId)?.name ?? "UNKNOWN",
	});

	PaymentProvider.openPopup(storeId, {
		clickSource: OfferAnalyticsClickSource.STORE,
	});
};

const claimFreePack = async (offer: ConversionOffer | null): Promise<void> => {
	if (!offer || !freeOfferAvailable.value) return;

	userStore.setIsDailyFreeRegularPackClaimed();
	PaymentProvider.emitGenericClick();

	if (offer.value) {
		for (const [key, value] of Object.entries(
			offer.value as Partial<Record<ResourceType, number>>,
		) as Array<[ResourceType, number]>) {
			increase(key, value, "event shop free");
		}
	}

	emitter.emit(EmitterEvents.ShopItemPurchased, {
		offerId: offer.id,
		offerType: offer.type,
	});

	await PaymentProvider.handleInAppPayment();
	OfferProvider.calculate(CalculationType.REGULAR);
};

watch(
	(): string => route.params.scrollTo as string,
	async (scrollTarget: string): Promise<void> => {
		scrollToRef(scrollTarget);
	},
	{ immediate: true },
);
</script>

<style lang="scss" scoped>
.shop {
	&__item {
		position: relative;
		width: 100%;
	}

	&-section {
		position: relative;
		@include flex-row-center;
		flex-wrap: wrap;
		gap: 1rem;
		width: 100%;

		&--sale {
			margin: 1rem 0;
		}

		.ref-section-wrapper {
			position: relative;
			width: 100%;
		}
		&__toast-message-buy {
			position: absolute;
			top: 50%;
			left: 40%;
		}
	}
}

.section-label {
	@include flex-row-center;
	position: relative;
	mask-image: linear-gradient(
		to right,
		transparent 2%,
		black 20%,
		black 80%,
		transparent 98%
	);
	width: 100%;
	height: 1.875rem;

	span {
		position: relative;
		z-index: 1;
		padding-bottom: 0.25rem;
		color: #f4d278;
		font-weight: 800;
		font-size: 1.25rem;
		line-height: 1.25rem;
		text-align: center;
		text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
		text-transform: uppercase;
	}

	&::before {
		position: absolute;
		top: 0;
		left: 0;
		@include background(
			url($path-textures + "silk-flag-texture-bg.avif"),
			cover,
			center center,
			no-repeat
		);
		z-index: 0;
		width: 100%;
		height: 100%;
		content: "";
	}

	&::after {
		position: absolute;
		top: 0;
		left: 0;
		mix-blend-mode: hard-light;
		background: rgba(78, 29, 97, 1);
		width: 100%;
		height: 100%;
		content: "";
	}
}

.text-button {
	:deep(.text-button) {
		gap: 0.5rem;
		padding: 0 1rem;
		width: unset;

		.dual-price {
			opacity: 0.9;
			font-size: 0.75rem;
		}
	}
}
</style>
